import { useEffect, useReducer } from 'react';

import { useUser } from '~/context/UserContext';

import garagesApi from '~/account/api/garages';
import { cloneDeep, filter, findIndex, forEach, map as _map } from 'lodash';

const garageReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'INIT':
            return {
                ...state,
                garages: payload.garages,
                loading: false,
                allVehicles: payload.allVehicles,
                errorMessage: payload.errorMessage
            };
        case 'SET_GARAGES': {
            return {
                ...state,
                garages: payload
            };
        }
        case 'DELETE_VEHICLE': {
            const garages = _map(state.garages, (garage) => {
                if (garage?.vehicles) {
                    garage.vehicles = filter(garage.vehicles, ({ vehicle }) => {
                        return vehicle.id !== payload;
                    });
                }

                return garage;
            });

            return {
                ...state,
                garages
            };
        }
        case 'ADD_VEHICLE': {
            const {
                vehicle: { account_id }
            } = payload;

            const garages = _map(state.garages, (garage) => {
                const { id } = garage;

                if (account_id === id) {
                    if (garage.vehicles) {
                        garage.vehicles.push(payload);
                    } else {
                        garage.vehicles = [payload];
                    }
                }

                return garage;
            });

            return {
                ...state,
                loading: true,
                garages
            };
        }
        case 'UPDATE_VEHICLE':
        case 'ADD_GARAGE': {
            const garages = cloneDeep(state.garages);

            garages.push(payload);

            return {
                ...state,
                garages
            };
        }
        case 'EDIT_GARAGE': {
            const garages = _map(state.garages, (garage) => {
                if (garage.id === payload.id) {
                    payload.vehicles = garage?.vehicles;
                    return payload;
                }

                return garage;
            });

            return {
                ...state,
                garages
            };
        }
        case 'SET_ALL_VEHICLES': {
            return {
                ...state,
                allVehicles: payload
            };
        }
        case 'SET_ERROR_MESSAGE':
            return {
                ...state,
                errorMessage: payload
            };
        case 'SET_LOADING':
            return {
                ...state,
                loading: payload
            };
        default:
            return state;
    }
};

const useGarage = () => {
    const { defaultVehicleId, setDefaultVehicleId } = useUser();

    const initialState = {
        garages: [],
        allVehicles: [],
        loading: true,
        errorMessage: ''
    };

    const [state, dispatch] = useReducer(
        garageReducer,
        initialState,
        () => initialState
    );

    useEffect(() => {
        const initGarages = async () => {
            const { data, errorMessage } = await garagesApi.getGarages();

            const accounts = data?.accounts;
            const allVehicles = [];

            if (accounts) {
                const vehicles = await Promise.all(
                    _map(accounts, ({ id }) =>
                        garagesApi.getVehiclesByAccount(id)
                    )
                );

                forEach(vehicles, ({ data: { vehicleData } }, index) => {
                    const accountIndex = findIndex(
                        accounts,
                        ({ id }) => id === accounts[index].id
                    );
                    accounts[accountIndex]['vehicles'] = vehicleData;

                    forEach(vehicleData, (vehicle) =>
                        allVehicles.push(vehicle)
                    );
                });
            }

            let payload = {
                garages: accounts,
                allVehicles,
                errorMessage: ''
            };

            if (errorMessage) {
                payload.errorMessage = errorMessage;
            }

            dispatch({
                type: 'INIT',
                payload
            });
        };
        if (state.loading) {
            initGarages();
        }
    }, [state.loading]);

    useEffect(() => {
        if (!defaultVehicleId && state.allVehicles.length) {
            let vehicleIndex = 0;
            const firstVehicleWithPlan = findIndex(
                state.allVehicles,
                'subscription'
            );
            if (firstVehicleWithPlan !== -1) {
                vehicleIndex = firstVehicleWithPlan;
            }
            setDefaultVehicleId(state.allVehicles[vehicleIndex].vehicle.id);
        }
    }, [defaultVehicleId, setDefaultVehicleId, state.allVehicles]);

    return {
        ...state,
        dispatch
    };
};

export default useGarage;
